import {
  type Editor,
  EditorContent,
  type Extensions,
  useEditor,
} from '@tiptap/react';
import { type InputProps } from 'antd';
import type React from 'react';
import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useInputController } from '../../hooks/useInputController.ts';
import { useFormItemEnabled } from '../../item/useFormItemEnabled.ts';
import { DisableEnterExtension } from './extensions/DisableEnterExtension.tsx';
import { type MenuGroup } from './menu/MenuGroup.tsx';
import { RichTextMenuBar } from './menu/RichTextMenuBar.tsx';
import { useRichTextMenuExclude } from './menu/RichTextMenuScopeContext.tsx';
import { resolveRichTextMenus } from './menu/resolveRichTextMenus.ts';
import { RichTextBaseExtensions } from './RichTextBaseExtensions.tsx';
import { RichTextInputWrapper } from './RichTextInputWrapper.tsx';

export interface IRichTextInputProps {
  name: string;
  /** Informazione sul tipo di campo che viene gestito dal componente. */
  rteItemTypeFieldName?: string;
  size?: InputProps['size'];
  disabled?: boolean;
  /*
   * Elenco dei gruppi di pulsanti da mostrare.
   * Se non viene inserito nulla vemgono mostrati solo i pulsanti di default ().
   */
  menus?: MenuGroup[];
  /**
   * Menu personalizzabile nella parte sinistra del componente.
   */
  extendedMenu?: (editor: Editor | null) => React.ReactNode;
  /**
   * Proprietà estese dell'editor
   */
  extensions?: Extensions;
  /*
   * Per l'inserimento di elementi extra al menu
   * che saranno posizionati a destra del menu.
   */
  extra?: React.ReactNode;
  /*
   * Per l'inserimento di elementi extra dopo il campo di testo
   * ma all'interno del componente di input.
   */
  footer?: React.ReactNode;

  /*
   * Numero di righe da mostrare nel campo di input.
   */
  rows?: number;
  /*
   * // TODO Eliminare maxHeight
   * Altezza massima del campo di input.
   * Sovrascrive il valore di default calcolato in base alla wievport.
   */
  maxHeight?: number;

  /*
   * @deprecated
   * // TODO Probabilmente si può eliminare il colore è gestito con una classe css
   * Da valorizzare quando è settato 'color' nel menus,
   * per passarlo al pulsante Colore nell'editor.
   * Se non valorizzato usiamo il colore primario del tema (StyledRteTheme).
   */
  color?: string;
}

export function RichTextInput(props: IRichTextInputProps) {
  const {
    name,
    rteItemTypeFieldName,
    menus: rawMenus,
    color,
    extra,
    footer,
    extensions,
    extendedMenu,
    ...inputProps
  } = props;

  const { enabled } = useFormItemEnabled();

  const {
    field: { value, onChange, onBlur, ref },
  } = useInputController(name, { enabled });

  const fieldType = useWatch({
    name: rteItemTypeFieldName ?? '',
    disabled: rteItemTypeFieldName == null,
  });

  const exclude = useRichTextMenuExclude();

  const menus = useMemo(
    () => resolveRichTextMenus({ menus: rawMenus, fieldType, exclude }),
    [fieldType, rawMenus, exclude],
  );

  const editor = useEditor({
    extensions: [
      // Disabilita l'inserimento di un nuovo paragrafo (accapo) con il tasto Enter nel caso sia Inline
      ...(menus?.includes('disable-enter') ? [DisableEnterExtension] : []),
      // Estensioni di base
      ...RichTextBaseExtensions,
      // Personalizzate es. per la gestione delle referenze
      ...(extensions ?? []),
    ],
    editable: props.disabled ? false : true,
    content: value,
    onBlur: ({ event }) => {
      onBlur();
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Get rid of the <p></p> that is added by tiptap
      onChange(html === '<p></p>' ? '' : html);
    },
    injectCSS: false,
    editorProps: {
      // Gestisco il paste per inserire il testo semplice
      handlePaste: (view, event) => {
        const text = event.clipboardData!.getData('text/plain');
        view.dispatch(view.state.tr.insertText(text));

        return true;
      },
      attributes: {
        class: 'rich-text-input-editor',
      },
    },
  });

  /**
   * Se il valore del campo cambia esternamente, aggiorniamo il contenuto
   * dell'editor. Questo è necessario per gestire il caso in cui il contenuto
   * viene modificato da un'altra parte (es. tramite gli `initialValues` del
   * form a seguito di un reload dell'entità, oppure in un Drawer utilizzato
   * per la modifica di più entità).
   */
  // TODO Verificare se possono esserci problemi di performance nel confronto
  // tra il valore del campo e quello dell'editor.
  useEffect(() => {
    if (value !== editor?.getHTML()) {
      editor?.commands.setContent(value);
    }
  }, [value]);

  return (
    <RichTextInputWrapper
      size="large"
      extra={extra}
      menuBar={
        <RichTextMenuBar
          editor={editor}
          menus={menus}
          extendedMenu={extendedMenu}
        />
      }
      {...inputProps}
    >
      <EditorContent editor={editor} />
      {footer && <div>{footer}</div>}
      {/* <RichTextFooterItem>{footer}</RichTextFooterItem> */}
    </RichTextInputWrapper>
  );
}
